import React, { useState } from "react";
import { Clock, CheckCircle, XCircle, ChevronRight, ChevronLeft } from "lucide-react";

/**
 * OvertimeRequestCard
 * Individual request card in the list view.
 */
const OvertimeRequestCard = ({ record, theme, isRTL, t, language, onClick }) => {
  const isDark = theme === "dark";
  const [hovered, setHovered] = useState(false);

  const approvalStatus = record.overallStatus || "pending";

  const overtimeMinutes = parseInt(record.overTime, 10) || 0;

  const formatOT = (mins) => {
    if (mins >= 60) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m > 0 ? `${h}${t.hour} ${m}${t.minutes}` : `${h}${t.hour}`;
    }
    return `${mins}${t.minutes}`;
  };

  const palette = {
    pending: {
      bg: isDark ? "rgba(245,158,11,0.08)" : "rgba(254,243,199,0.7)",
      border: isDark ? "rgba(245,158,11,0.25)" : "rgba(245,158,11,0.3)",
      color: isDark ? "#fcd34d" : "#92400e",
      dot: "#f59e0b",
      label: t.pendingApproval,
    },
    approved: {
      bg: isDark ? "rgba(16,185,129,0.08)" : "rgba(209,250,229,0.7)",
      border: isDark ? "rgba(16,185,129,0.25)" : "rgba(16,185,129,0.3)",
      color: isDark ? "#6ee7b7" : "#065f46",
      dot: "#10b981",
      label: t.approvedStatus,
    },
    rejected: {
      bg: isDark ? "rgba(239,68,68,0.08)" : "rgba(254,226,226,0.7)",
      border: isDark ? "rgba(239,68,68,0.25)" : "rgba(239,68,68,0.3)",
      color: isDark ? "#fca5a5" : "#991b1b",
      dot: "#ef4444",
      label: t.rejectedStatus,
    },
  };

  const p = palette[approvalStatus];

  // Generate initials avatar
  const name = record.employeeName || "";
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

  const avatarColors = [
    ["#6366f1", "#4f46e5"],
    ["#ec4899", "#db2777"],
    ["#f59e0b", "#d97706"],
    ["#10b981", "#059669"],
    ["#3b82f6", "#2563eb"],
    ["#8b5cf6", "#7c3aed"],
  ];
  const colorIdx = name.length % avatarColors.length;
  const [avatarC1, avatarC2] = avatarColors[colorIdx];

  const ArrowIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div
      onClick={() => onClick(record)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "16px",
        borderRadius: "14px",
        cursor: "pointer",
        background: hovered
          ? isDark
            ? "rgba(255,255,255,0.06)"
            : "rgba(0,0,0,0.03)"
          : isDark
          ? "rgba(255,255,255,0.02)"
          : "rgba(0,0,0,0.01)",
        border: `1px solid ${
          hovered
            ? isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)"
            : isDark
            ? "rgba(255,255,255,0.06)"
            : "rgba(0,0,0,0.04)"
        }`,
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: hovered ? "translateY(-1px)" : "none",
        boxShadow: hovered
          ? isDark
            ? "0 4px 16px rgba(0,0,0,0.3)"
            : "0 4px 16px rgba(0,0,0,0.06)"
          : "none",
        flexDirection: isRTL ? "row-reverse" : "row",
      }}
    >
      {/* Avatar */}
      {record.employeeImage || record.profileImage || record.avatar ? (
        <img
          src={record.employeeImage || record.profileImage || record.avatar}
          alt={record.employeeName || ""}
          style={{
            width: "44px",
            height: "44px",
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
            border: `2px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)"}`,
          }}
          onError={(e) => {
            // Hide broken image, show initials fallback
            e.target.style.display = "none";
            if (e.target.nextSibling?.style) e.target.nextSibling.style.display = "flex";
          }}
        />
      ) : null}
      <div
        style={{
          width: "44px",
          height: "44px",
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${avatarC1}, ${avatarC2})`,
          display: (record.employeeImage || record.profileImage || record.avatar) ? "none" : "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: "15px",
          fontWeight: 700,
          fontFamily: "'Cairo', sans-serif",
          flexShrink: 0,
          boxShadow: `0 4px 12px ${avatarC1}40`,
        }}
      >
        {initials}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "4px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: isDark ? "#f1f5f9" : "#1e293b",
              fontFamily: "'Cairo', sans-serif",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {record.employeeName || record.employeeId}
          </span>
          {/* Overtime badge */}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 8px",
              borderRadius: "8px",
              background: isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)",
              color: isDark ? "#a5b4fc" : "#4f46e5",
              fontSize: "12px",
              fontWeight: 700,
              fontFamily: "'Cairo', sans-serif",
              flexShrink: 0,
            }}
          >
            <Clock size={11} />
            {formatOT(overtimeMinutes)}
          </span>
        </div>

        {/* Row 2: date + status */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            {/* Date */}
            <span
              style={{
                fontSize: "12px",
                color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {record.date
                ? new Date(record.date).toLocaleDateString(
                    isRTL ? "ar-EG" : "en-US",
                    { month: "short", day: "numeric" }
                  )
                : "-"}
            </span>
            {/* Times */}
            <span
              style={{
                fontSize: "11px",
                color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
                fontFamily: "'Courier New', monospace",
              }}
            >
              {record.checkIn || "-"} → {record.checkOut || "-"}
            </span>
          </div>

          {/* Approval status badges */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              alignItems: isRTL ? "flex-start" : "flex-end",
            }}
          >
            <span
              style={{
                fontSize: "10px",
                fontWeight: 500,
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {t.pendingApproval}:{" "}
              <span
                style={{
                  color:
                    approvalStatus === "approved"
                      ? "#10b981"
                      : approvalStatus === "rejected"
                      ? "#ef4444"
                      : "#f59e0b",
                  fontWeight: 600,
                }}
              >
                {approvalStatus === "approved"
                  ? t.approvedStatus
                  : approvalStatus === "rejected"
                  ? t.rejectedStatus
                  : t.pendingApproval}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Arrow */}
      <ArrowIcon
        size={16}
        color={isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)"}
        style={{
          flexShrink: 0,
          transition: "transform 0.2s ease",
          transform: hovered
            ? isRTL
              ? "translateX(-3px)"
              : "translateX(3px)"
            : "none",
        }}
      />
    </div>
  );
};

export default OvertimeRequestCard;
